// Typed dashboard API client. All calls send the session cookie (credentials: include)
// so they work cross-origin from app.<root> to api.<root>.
import type {
  AccountTier,
  ActivityResponse,
  BuyKeepsRequest,
  CheckoutResponse,
  CreateKeepRequest,
  DashboardKeepDTO,
  DashboardMomentsResponse,
  DashboardPhotosResponse,
  GalleryCountsResponse,
  LoginRequest,
  LogoUploadResponse,
  MomentsResponse,
  PhotoModeration,
  PortalResponse,
  SessionResponse,
  SignupRequest,
  SubscribeRequest,
  TrendResponse,
  UpdateKeepRequest,
  UpdateMomentsRequest,
} from "@wedding/shared";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = `${method} ${path} -> ${res.status}`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data?.error) detail = data.error;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(detail, res.status);
  }
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

export const api = {
  me: () => req<SessionResponse>("GET", "/api/auth/me"),
  signup: (b: SignupRequest) => req<SessionResponse>("POST", "/api/auth/signup", b),
  login: (b: LoginRequest) => req<SessionResponse>("POST", "/api/auth/login", b),
  logout: () => req<{ ok: boolean }>("POST", "/api/auth/logout"),
  magicLink: (email: string) => req<{ ok: boolean }>("POST", "/api/auth/magic-link", { email }),
  verifyMagicLink: (token: string) => req<SessionResponse>("POST", "/api/auth/magic-link/verify", { token }),

  listKeeps: () => req<{ keeps: DashboardKeepDTO[] }>("GET", "/api/dashboard/keeps"),
  createKeep: (b: CreateKeepRequest) => req<DashboardKeepDTO>("POST", "/api/dashboard/keeps", b),
  updateKeep: (id: string, b: UpdateKeepRequest) =>
    req<DashboardKeepDTO>("PATCH", `/api/dashboard/keeps/${id}`, b),
  deleteKeep: (id: string) => req<{ ok: boolean }>("DELETE", `/api/dashboard/keeps/${id}`),

  listPhotos: (id: string, cursor?: string) =>
    req<DashboardPhotosResponse>(
      "GET",
      `/api/dashboard/keeps/${id}/photos${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  deletePhoto: (id: string, photoId: string) =>
    req<{ ok: boolean }>("DELETE", `/api/dashboard/keeps/${id}/photos/${photoId}`),
  setModeration: (id: string, photoId: string, moderation: PhotoModeration) =>
    req<{ ok: boolean }>("PATCH", `/api/dashboard/keeps/${id}/photos/${photoId}`, { moderation }),
  listMoments: (id: string) => req<DashboardMomentsResponse>("GET", `/api/dashboard/keeps/${id}/moments`),
  updateMoments: (id: string, body: UpdateMomentsRequest) =>
    req<{ ok: boolean }>("PATCH", `/api/dashboard/keeps/${id}/moments`, body),
  activity: (id: string) => req<ActivityResponse>("GET", `/api/dashboard/keeps/${id}/activity`),
  trend: (id: string) => req<TrendResponse>("GET", `/api/dashboard/keeps/${id}/trend`),

  updateProfile: (b: { name?: string; email?: string }) => req<SessionResponse>("PATCH", "/api/auth/profile", b),
  changePassword: (b: { currentPassword: string; newPassword: string }) =>
    req<{ ok: boolean }>("POST", "/api/auth/password", b),
  logoUploadUrl: (id: string, contentType: string) =>
    req<LogoUploadResponse>("POST", `/api/dashboard/keeps/${id}/logo-upload-url`, { contentType }),

  // Public read endpoints (event-scoped) reused for the admin overview.
  galleryCounts: (keepId: string) => req<GalleryCountsResponse>("GET", `/api/gallery/counts?keepId=${keepId}`),
  moments: (keepId: string) => req<MomentsResponse>("GET", `/api/moments?keepId=${keepId}`),

  subscribe: (tier: AccountTier) => req<CheckoutResponse>("POST", "/api/billing/subscribe", { tier } as SubscribeRequest),
  buyKeeps: (packId: string) => req<CheckoutResponse>("POST", "/api/billing/buy-keeps", { packId } as BuyKeepsRequest),
  portal: () => req<PortalResponse>("POST", "/api/billing/portal"),
};

/** Upload a blob to a presigned R2 URL with the exact signed headers. */
export async function putToR2(uploadUrl: string, headers: Record<string, string>, file: Blob): Promise<void> {
  const res = await fetch(uploadUrl, { method: "PUT", headers, body: file });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
}

/** Fetch a photo as a blob and save it to the user's device. */
export async function downloadFile(url: string, name: string): Promise<boolean> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const obj = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = obj;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(obj);
    return true;
  } catch {
    window.open(url, "_blank", "noopener");
    return false;
  }
}
